/**
 * Debug overlays drawn in the world (06 §8): what the sim believes, laid over what the player
 * sees. Six layers, each off by default and each built only when first switched on; with
 * every layer off, `update` and `handle` return at once and nothing has been allocated.
 *
 *   nav         blocked / costly cells as a dithered ground grid round the camera
 *   colliders   every prop's footprint and height
 *   vision      each soldier's cone and range (red while he sees the ranger)
 *   detection   the detection meter as a bar over each head
 *   noise       rings for recent gunfire, blasts and shouts (from `handle(events)`)
 *   paths       the waypoints each soldier still has ahead of him
 */

import type { Scene } from 'three';
import type { Statics } from '../../sim/statics';
import { type SimEvent, TICK_RATE, type World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import type { Palette } from '../palette';
import {
  drawColliders, drawDetection, drawNoise, drawPaths, drawVision, type GroundY, NOISE_SECONDS, noiseOf, type NoiseRing,
} from './layers';
import { LineBuffer } from './lineBuffer';
import { NavLayer } from './navLayer';

export const DEBUG_LAYERS = ['nav', 'colliders', 'vision', 'detection', 'noise', 'paths'] as const;
export type DebugLayerName = (typeof DEBUG_LAYERS)[number];
type LineLayerName = Exclude<DebugLayerName, 'nav'>;

/** Segments each line layer may draw in a frame; more than this is dropped, not grown. */
const CAPACITY: Record<LineLayerName, number> = {
  colliders: 6000, vision: 1200, detection: 200, noise: 800, paths: 1500,
};
const MAX_RINGS = 16;

export class DebugLayers {
  private readonly enabled = new Set<DebugLayerName>();
  private readonly lines = new Map<LineLayerName, LineBuffer>();
  private nav: NavLayer | null = null;
  private readonly rings: NoiseRing[] = [];
  private lastTick = -1;

  /** `groundY` drapes the overlays over the terrain (`StaticWorld.groundY`); without it they lie on y = 0. */
  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    private readonly groundY: GroundY = () => 0,
  ) {}

  /** Objects this overlay has created so far: 0 until a layer is first enabled. */
  get allocated(): number {
    return this.lines.size + (this.nav ? 1 : 0);
  }

  isEnabled(name: DebugLayerName): boolean {
    return this.enabled.has(name);
  }

  setEnabled(name: DebugLayerName, on: boolean): void {
    if (on) this.enabled.add(name);
    else this.enabled.delete(name);
    if (name === 'nav') {
      if (on && !this.nav) this.nav = new NavLayer(this.scene, this.palette, this.groundY);
      if (!on && this.nav) {
        this.nav.dispose();
        this.nav = null;
      }
      return;
    }
    let buffer = this.lines.get(name);
    if (on && !buffer) {
      buffer = new LineBuffer(CAPACITY[name], name);
      this.lines.set(name, buffer);
      this.scene.add(buffer.lines);
    }
    if (buffer) buffer.lines.visible = on;
    if (name === 'noise' && !on) this.rings.length = 0;
  }

  /** This frame's sim events: only the `noise` layer listens, and only while it is on. */
  handle(events: readonly SimEvent[]): void {
    if (!this.enabled.has('noise')) return;
    for (const e of events) {
      const noise = noiseOf(e);
      if (!noise) continue;
      if (this.rings.length >= MAX_RINGS) this.rings.shift();
      this.rings.push({ ...noise, age: 0 });
    }
  }

  update(world: World, statics: Statics, camera: PixelCamera): void {
    if (this.enabled.size === 0) return;
    const fx = camera.focus.x;
    const fz = camera.focus.z;
    const { palette, groundY } = this;
    this.nav?.update(statics, fx, fz);
    this.draw('colliders', (out) => drawColliders(out, palette, statics, fx, fz, groundY));
    this.draw('vision', (out) => drawVision(out, palette, world, statics, fx, fz, groundY));
    this.draw('detection', (out) => drawDetection(out, palette, world, fx, fz, groundY));
    this.draw('paths', (out) => drawPaths(out, palette, world, fx, fz, groundY));
    if (this.enabled.has('noise')) {
      // Rings age on the sim's clock, so they hold still while the game is paused.
      const dt = this.lastTick < 0 ? 0 : Math.max(0, world.tick - this.lastTick) / TICK_RATE;
      for (const ring of this.rings) ring.age += dt;
      while (this.rings[0] && this.rings[0].age >= NOISE_SECONDS) this.rings.shift();
      this.draw('noise', (out) => drawNoise(out, palette, this.rings, groundY));
    }
    this.lastTick = world.tick;
  }

  private draw(name: LineLayerName, fill: (out: LineBuffer) => void): void {
    const out = this.lines.get(name);
    if (!out || !this.enabled.has(name)) return;
    out.begin();
    fill(out);
    out.end();
  }

  dispose(): void {
    for (const buffer of this.lines.values()) buffer.dispose();
    this.lines.clear();
    this.nav?.dispose();
    this.nav = null;
    this.enabled.clear();
    this.rings.length = 0;
  }
}
